import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/* Editable site content. Layering (lowest → highest precedence):
     DEFAULT_CONTENT  →  /site-content.json (published, site-wide)  →  localStorage (this browser)
   The /editsite editor writes localStorage (live preview on this device) and can
   export the JSON to commit as public/site-content.json for all visitors. */

export const DEFAULT_CONTENT = {
  betaDate: "2026-10-20", // planned private-beta release
  contactEmail: "contact@sajilohealth.com",
  facebookUrl: "https://www.facebook.com/share/1cUJDNSVZc/?mibextid=wwXIfr",
  linkedinUrl: "", // add via /editsite
  traction: {
    clinics: "—",
    patients: "—",
    transactions: "—",
  },
};

export const STORAGE_KEY = "sh_site_content_v1";
const REMOTE_URL = "/site-content.json";

export function deepMerge(base, over) {
  if (!over || typeof over !== "object") return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(over).forEach((k) => {
    const v = over[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = deepMerge(base[k] || {}, v);
    } else if (v !== undefined && v !== null) {
      out[k] = v;
    }
  });
  return out;
}

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

const ContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(() => deepMerge(DEFAULT_CONTENT, readLocal()));

  // Published, site-wide overlay (applied beneath this browser's local edits).
  useEffect(() => {
    let cancelled = false;
    fetch(REMOTE_URL, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((remote) => {
        if (cancelled || !remote || typeof remote !== "object") return;
        setContent(deepMerge(deepMerge(DEFAULT_CONTENT, remote), readLocal()));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Live sync across tabs (edit in /editsite, watch it change on the site).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setContent(deepMerge(DEFAULT_CONTENT, readLocal()));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const updateContent = useCallback((partial) => {
    setContent((prev) => {
      const next = deepMerge(prev, partial);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  }, []);

  const resetContent = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
    setContent(deepMerge(DEFAULT_CONTENT, null));
  }, []);

  return (
    <ContentContext.Provider value={{ content, updateContent, resetContent }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within SiteContentProvider");
  return ctx;
}
